/**
 * One Mapbox map for Live and Map on desktop (RUT-4535, Motion & Flow Oct 16).
 *
 * Phones keep their own maps. On lg+ the first page to attach creates the
 * instance; the other page reparents the same canvas. The camera is snapshotted
 * when we leave both pages and restored when we come back, so a hop to
 * Customers and back does not rebuild tiles.
 */
import type mapboxgl from 'mapbox-gl';

export const DESKTOP_MAP_SHARE_QUERY = '(min-width: 1024px)';
export const SHARED_MAP_PANEL_SLIDE_MS = 250;

const PARK_ID = 'ruta-desktop-shared-map-park';

export type SharedMapCamera = {
  center: [number, number];
  zoom: number;
  bearing: number;
  pitch: number;
};

type SharedMapState = {
  map: mapboxgl.Map | null;
  createCount: number;
  camera: SharedMapCamera | null;
  resizeStop: (() => void) | null;
  lastAttachWasAdopt: boolean;
};

const state: SharedMapState = {
  map: null,
  createCount: 0,
  camera: null,
  resizeStop: null,
  lastAttachWasAdopt: false,
};

export function lastSharedMapAttachWasAdopt(): boolean {
  return state.lastAttachWasAdopt;
}

/** In-viewport, not off-screen: Chrome drops WebGL for canvases at left:-9999px. */
const PARK_STYLE =
  'position:fixed;left:0;top:0;width:400px;height:300px;z-index:-1;opacity:0;overflow:hidden;pointer-events:none;';

export function isDesktopMapShare(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(DESKTOP_MAP_SHARE_QUERY).matches;
}

export function getSharedDesktopMap(): mapboxgl.Map | null {
  return state.map;
}

export function sharedDesktopMapCreateCount(): number {
  return state.createCount;
}

export function getSharedDesktopCamera(): SharedMapCamera | null {
  return state.camera;
}

export function snapshotSharedMapCamera(map: mapboxgl.Map | null = state.map): void {
  if (!map) return;
  try {
    const c = map.getCenter();
    state.camera = {
      center: [c.lng, c.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
  } catch {
    // Map mid-teardown.
  }
}

export function restoreSharedMapCamera(map: mapboxgl.Map | null = state.map): void {
  if (!map || !state.camera) return;
  try {
    map.jumpTo({
      center: state.camera.center,
      zoom: state.camera.zoom,
      bearing: state.camera.bearing,
      pitch: state.camera.pitch,
    });
  } catch {
    // Style not ready yet — the next resize pass will try again.
  }
}

function parkHost(): HTMLDivElement {
  let el = document.getElementById(PARK_ID) as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = PARK_ID;
    el.setAttribute('data-testid', 'desktop-shared-map-park');
    el.style.cssText = PARK_STYLE;
    document.body.appendChild(el);
  }
  return el;
}

function fillSlot(slot: HTMLElement, container: HTMLElement): void {
  if (container.parentElement !== slot) slot.appendChild(container);
  container.style.cssText = 'width:100%;height:100%;';
}

function makeHost(): HTMLDivElement {
  const host = document.createElement('div');
  host.setAttribute('data-shared-map-host', 'true');
  host.style.cssText = 'width:100%;height:100%;';
  return host;
}

function slotHasSize(slot: HTMLElement): boolean {
  return slot.clientWidth >= 8 && slot.clientHeight >= 8;
}

function kickMap(map: mapboxgl.Map): void {
  try {
    map.resize();
    restoreSharedMapCamera(map);
    (map as mapboxgl.Map & { triggerRepaint?: () => void }).triggerRepaint?.();
  } catch {
    // Map mid-teardown.
  }
}

function watchWebglContext(map: mapboxgl.Map): void {
  const onRestored = () => kickMap(map);
  try {
    map.on('webglcontextrestored', onRestored);
    const canvas = map.getCanvas?.();
    canvas?.addEventListener('webglcontextrestored', onRestored);
    canvas?.addEventListener('webglcontextlost', (event: Event) => {
      event.preventDefault();
    });
  } catch {
    // Map mid-teardown.
  }
}

/**
 * An adopted Live↔Map instance already fired `load` on first create. Waiting
 * for it again leaves the page with no markers until a reload (RUT-4535).
 */
export function whenSharedMapReady(map: mapboxgl.Map, cb: () => void): () => void {
  let cancelled = false;
  const fire = () => {
    if (cancelled) return;
    cancelled = true;
    cb();
  };

  if (state.lastAttachWasAdopt) {
    fire();
    return () => {
      cancelled = true;
    };
  }

  try {
    if (map.isStyleLoaded?.() || map.loaded?.()) {
      fire();
      return () => {
        cancelled = true;
      };
    }
  } catch {
    // Style mid-swap — wait for idle.
  }

  const onReady = () => fire();
  if (typeof map.once === 'function') {
    map.once('idle', onReady);
    map.once('load', onReady);
  } else {
    map.on('idle', onReady);
    map.on('load', onReady);
  }
  return () => {
    cancelled = true;
    map.off?.('idle', onReady);
    map.off?.('load', onReady);
  };
}

export function sharedMapIsInSlot(slot: HTMLElement | null | undefined): boolean {
  const map = state.map;
  if (!map || !slot) return false;
  try {
    const el = map.getContainer();
    return el === slot || slot.contains(el);
  } catch {
    return false;
  }
}

function parkMap(): void {
  const map = state.map;
  if (!map) return;
  snapshotSharedMapCamera(map);
  const container = map.getContainer();
  const park = parkHost();
  if (container.parentElement !== park) park.appendChild(container);
  kickMap(map);
}

/** Resize on every frame for the panel slide so the canvas tracks the hole. */
export function resizeSharedMapDuringSlide(
  map: mapboxgl.Map | null = state.map,
  slot?: HTMLElement | null,
  ms = SHARED_MAP_PANEL_SLIDE_MS,
): void {
  if (!map) return;
  state.resizeStop?.();
  const started = performance.now();
  let frame = 0;
  const tick = () => {
    if (!slot || slotHasSize(slot)) {
      kickMap(map);
    }
    if (performance.now() - started < ms) {
      frame = requestAnimationFrame(tick);
    }
  };
  frame = requestAnimationFrame(tick);
  let observer: ResizeObserver | null = null;
  if (slot && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => {
      if (!slotHasSize(slot)) return;
      kickMap(map);
    });
    observer.observe(slot);
  }
  state.resizeStop = () => {
    cancelAnimationFrame(frame);
    observer?.disconnect();
    state.resizeStop = null;
  };
}

export function attachDesktopSharedMap(
  slot: HTMLElement,
  create: (container: HTMLElement) => mapboxgl.Map,
): mapboxgl.Map {
  if (state.map) {
    state.lastAttachWasAdopt = true;
    try {
      (state.map as mapboxgl.Map & { stop?: () => void }).stop?.();
    } catch {
      // Mid-teardown.
    }
    snapshotSharedMapCamera(state.map);
    const container = state.map.getContainer();
    fillSlot(slot, container);
    slot.dataset.rutaSharedZoom = state.camera ? String(state.camera.zoom) : 'adopt';
    resizeSharedMapDuringSlide(state.map, slot);
    kickMap(state.map);
    return state.map;
  }

  state.lastAttachWasAdopt = false;

  // Inner host, not the React slot — parking must not steal a node React owns.
  const host = makeHost();
  slot.appendChild(host);
  const map = create(host);
  state.map = map;
  state.createCount += 1;
  watchWebglContext(map);
  resizeSharedMapDuringSlide(map, slot);
  return map;
}

/**
 * Live or Map is leaving. Park synchronously in layout cleanup so the
 * canvas stays in the document (and keeps its WebGL context) before paint.
 * A same-flush attach on the other page pulls it out of the park.
 */
export function releaseDesktopSharedMap(slot?: HTMLElement | null): void {
  const map = state.map;
  if (!map) return;
  const container = map.getContainer();
  if (slot && container.parentElement && container.parentElement !== slot) {
    return;
  }
  parkMap();
}

export function destroyDesktopSharedMap(): void {
  state.resizeStop?.();
  if (state.map) {
    try {
      snapshotSharedMapCamera(state.map);
      state.map.remove();
    } catch {
      // Already gone.
    }
    state.map = null;
  }
  state.camera = null;
  const park = document.getElementById(PARK_ID);
  park?.remove();
}

/** Test-only: wipe module state between cases. */
export function resetDesktopSharedMapForTests(): void {
  destroyDesktopSharedMap();
  state.createCount = 0;
  state.camera = null;
  state.lastAttachWasAdopt = false;
}
